import React, { useState, useEffect } from 'react';
import { Brain, FileText, Video, Upload, Database, DollarSign, FileBarChart } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { knowledgeService } from '@/services/knowledgeService';
import { costTrackingService } from '@/services/costTrackingService';
import { apiClient } from '@/lib/apiClient';
import '@/utils/testApiAuth'; // Import test functions for console testing
import { AppFAQManager } from './AppFAQManager';
import { ReportRecipesManager } from './ReportRecipesManager';
import { SalesTrainingManager } from './SalesTrainingManager';
import { CustomDocumentsManager } from './CustomDocumentsManager';
import { CRMDataConfig } from './CRMDataConfig';
import { CostDashboard } from './CostDashboard';

export const KnowledgeBaseSettings: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const [activeTab, setActiveTab] = useState('kb-app-faq');
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Joules is the main/platform tenant that owns shared knowledge content.
  const SHARED_TENANT_ID = '511f29e8-bef3-4065-9927-777d6eafe7f1';
  // AI cost is a platform concern — only surface it under the Joules tenant.
  const isJoulesTenant = currentTenant?.tenant_id === SHARED_TENANT_ID;

  useEffect(() => {
    // Pre-authenticate with backend API when component mounts
    // Note: Authentication is handled automatically by apiClient
    /*
    const initializeAPI = async () => {
      try {
        if (!apiClient.isAuthenticated()) {
          await apiClient.authenticate();
        }
      } catch (error) {
        console.error('Failed to authenticate with backend API:', error);
        // Continue anyway - individual API calls will handle authentication
      }
    };

    initializeAPI();
    */
    loadStats();
  }, [user]);

  const loadStats = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Get document counts and costs with error handling
      const [documents, monthlyUsage] = await Promise.all([
        knowledgeService.getDocuments(SHARED_TENANT_ID).catch(err => {
          console.warn('Error loading documents:', err);
          return [];
        }),
        costTrackingService.getMonthlyUsage(SHARED_TENANT_ID).catch(err => {
          console.warn('Error loading monthly usage:', err);
          return [];
        })
      ]);

      // Group documents by source type
      const docsBySource = documents.reduce((acc: any, doc: any) => {
        acc[doc.source_type] = (acc[doc.source_type] || 0) + 1;
        return acc;
      }, {});

      // Calculate total monthly cost
      const totalCost = monthlyUsage.reduce((sum: number, item: any) => 
        sum + (parseFloat(item.total_cost) || 0), 0
      );

      setStats({
        documents: docsBySource,
        totalDocuments: documents.length,
        monthlyCost: totalCost,
        monthlyUsage
      });
    } catch (error) {
      console.error('Error loading knowledge base stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'app_faq': return <FileText className="h-4 w-4" />;
      case 'report_recipe': return <FileBarChart className="h-4 w-4" />;
      case 'sales_training': return <Video className="h-4 w-4" />;
      case 'custom_docs': return <Upload className="h-4 w-4" />;
      case 'crm_data': return <Database className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'app_faq': return 'App FAQs';
      case 'report_recipe': return 'Report Recipes';
      case 'sales_training': return 'Sales Training';
      case 'custom_docs': return 'Custom Documents';
      case 'crm_data': return 'CRM Data';
      default: return sourceType;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className={`grid grid-cols-1 ${isJoulesTenant ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <Brain className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalDocuments || 0}</div>
            <p className="text-xs text-muted-foreground">
              Across all knowledge sources
            </p>
          </CardContent>
        </Card>

        {/* AI cost shown only under the Joules (platform) tenant. */}
        {isJoulesTenant && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(stats?.monthlyCost || 0).toFixed(4)}</div>
            <p className="text-xs text-muted-foreground">
              AI operations this month
            </p>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Sources</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4</div>
            <p className="text-xs text-muted-foreground">
              Knowledge brain types
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <Badge variant="outline" className="text-green-600 border-green-600">
              Active
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-sm">pgvector enabled</div>
            <p className="text-xs text-muted-foreground">
              Vector search operational
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Source Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Knowledge Sources Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { type: 'app_faq', name: 'App FAQs', description: 'Application help & guidance' },
              { type: 'sales_training', name: 'Sales Training', description: 'Video transcripts & materials' },
              { type: 'custom_docs', name: 'Custom Documents', description: 'PDFs, Word docs, text files' },
              { type: 'crm_data', name: 'CRM Data', description: 'Live project & customer data' }
            ].map((source) => (
              <div key={source.type} className="border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {getSourceIcon(source.type)}
                  <span className="font-medium text-sm">{source.name}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{source.description}</p>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="text-xs">
                    {stats?.documents?.[source.type] || 0} docs
                  </Badge>
                  <Badge variant={source.type === 'sales_training' ? 'default' : 'outline'} className="text-xs">
                    {source.type === 'sales_training' ? 'Citations' : 'No Citations'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Source Management Tabs */}
      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className={`grid w-full ${isJoulesTenant ? 'grid-cols-6' : 'grid-cols-5'}`}>
            <TabsTrigger value="kb-app-faq" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">App FAQs</span>
            </TabsTrigger>
            <TabsTrigger value="kb-report-recipes" className="flex items-center gap-2">
              <FileBarChart className="h-4 w-4" />
              <span className="hidden sm:inline">Report Recipes</span>
            </TabsTrigger>
            <TabsTrigger value="kb-sales-training" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              <span className="hidden sm:inline">Sales Training</span>
            </TabsTrigger>
            <TabsTrigger value="kb-custom-docs" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="kb-crm-config" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">CRM Data</span>
            </TabsTrigger>
            {isJoulesTenant && (
            <TabsTrigger value="kb-costs" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Costs</span>
            </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="kb-app-faq" className="space-y-4">
            <AppFAQManager onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="kb-report-recipes" className="space-y-4">
            <ReportRecipesManager onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="kb-sales-training" className="space-y-4">
            <SalesTrainingManager onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="kb-custom-docs" className="space-y-4">
            <CustomDocumentsManager onUpdate={loadStats} />
          </TabsContent>

          <TabsContent value="kb-crm-config" className="space-y-4">
            <CRMDataConfig onUpdate={loadStats} />
          </TabsContent>

          {isJoulesTenant && (
          <TabsContent value="kb-costs" className="space-y-4">
            <CostDashboard />
          </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};